import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { useAuth, useTheme, api, API } from "../App";
import {
  Copy,
  Check,
  SkipForward,
  RefreshCw,
  Sun,
  Moon,
  Settings,
  History,
  LogOut,
  Sparkles,
  ChevronDown,
  BookOpen,
  Heart,
  Image,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const TOPIC_LABELS = {
  "ai-ml": "AI & ML",
  "cloud": "Cloud",
  "devops": "DevOps",
  "security": "Security",
  "web-dev": "Web Dev",
  "mobile": "Mobile",
  "data-science": "Data Science",
  "blockchain": "Blockchain",
  "engineering": "Engineering",
  "product": "Product",
  "ux-design": "UX Design",
  "startup": "Startup",
  "leadership": "Leadership",
  "career": "Career",
  "open-source": "Open Source",
  "programming": "Programming",
};

const cleanPostContent = (text) => {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")   // **bold** → plain
    .replace(/\*(.*?)\*/g, "$1")        // *italic* → plain
    .replace(/^---+\s*VARIANT.*$/gm, "") // --- VARIANT: ... --- headers
    .replace(/^---+$/gm, "")            // standalone --- separators
    .replace(/^#{1,6}\s+/gm, "")        // ## headings → plain
    .replace(/^\s*[-*]\s+/gm, "• ")     // - bullets → •
    .replace(/\n{3,}/g, "\n\n")         // collapse excess blank lines
    .trim();
};

const IMAGE_TYPE_LABELS = {
  infographic: "Infographic",
  carousel: "Carousel",
  dalle: "AI Image",
};

const PostCard = ({ post, user, onCopy, onMarkUsed, onSkip, copiedId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const content = cleanPostContent(post.variant_content);
  const shouldTruncate = content.length > 400;
  const displayContent = shouldTruncate && !isExpanded
    ? content.substring(0, 400)
    : content;

  // Image state
  const backendUrl = API.replace(/\/api$/, "");
  const [imageUrl, setImageUrl] = useState(
    post.image_path ? `${backendUrl}/static/images/${post.image_path}` : null
  );
  const [carouselUrls, setCarouselUrls] = useState(
    post.carousel_paths ? post.carousel_paths.map(p => `${backendUrl}/static/images/${p}`) : []
  );
  const [imageStatus, setImageStatus] = useState(post.image_status || null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);

  const handleGenerateImage = async () => {
    setIsGenerating(true);
    try {
      const res = await api.post(`/posts/${post.id}/generate-image`);
      if (res.data.image_url) setImageUrl(res.data.image_url);
      if (res.data.carousel_urls) setCarouselUrls(res.data.carousel_urls);
      setImageStatus("done");
    } catch {
      toast.error("Image generation failed");
      setImageStatus("failed");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div
      className="bg-card rounded-lg border border-border shadow-sm overflow-hidden"
      data-testid={`post-card-${post.id}`}
    >
      {/* LinkedIn-style header */}
      <div className="flex items-start gap-3 px-4 pt-3 pb-2">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full shrink-0" />
        ) : (
          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold text-lg shrink-0">
            {user?.name?.charAt(0) || "U"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-foreground leading-tight">{user?.name || "You"}</p>
          <p className="text-xs text-muted-foreground leading-snug mt-0.5 truncate">
            {TOPIC_LABELS[post.topic] || post.topic}
            {post.angle && ` \u00b7 ${post.angle}`}
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            {post.story_potential_score != null && (
              <div className="group/tip relative flex items-center gap-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-400 cursor-default">
                <Sparkles className="w-3 h-3" />
                {post.story_potential_score}/10
                <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-md bg-popover text-popover-foreground text-xs px-2 py-1 shadow-md opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 z-10">
                  Story Potential
                </span>
              </div>
            )}
            {post.story_potential_score != null && post.relatability_score != null && (
              <span className="text-[11px] text-muted-foreground">&middot;</span>
            )}
            {post.relatability_score != null && (
              <div className="group/tip2 relative flex items-center gap-0.5 text-[11px] font-medium text-rose-600 dark:text-rose-400 cursor-default">
                <Heart className="w-3 h-3" />
                {post.relatability_score}/10
                <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-md bg-popover text-popover-foreground text-xs px-2 py-1 shadow-md opacity-0 group-hover/tip2:opacity-100 transition-opacity duration-150 z-10">
                  Relatability
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-2">
        <p className="text-foreground whitespace-pre-wrap leading-relaxed text-sm">
          {displayContent}
          {shouldTruncate && !isExpanded && (
            <button
              onClick={() => setIsExpanded(true)}
              className="text-muted-foreground hover:text-primary hover:underline ml-0.5"
              data-testid={`expand-btn-${post.id}`}
            >
              ...see more
            </button>
          )}
        </p>
        {shouldTruncate && isExpanded && (
          <button
            onClick={() => setIsExpanded(false)}
            className="text-muted-foreground text-sm hover:text-primary hover:underline mt-1"
            data-testid={`expand-btn-${post.id}`}
          >
            Show less
          </button>
        )}
      </div>

      {/* Takeaway */}
      {post.takeaway && (
        <div className="mx-4 mb-2 p-3 bg-accent/40 rounded-lg border-l-[3px] border-l-primary">
          <p className="text-xs text-primary mb-1 font-semibold uppercase tracking-wide">Key Takeaway</p>
          <p className="text-sm text-foreground">{post.takeaway}</p>
        </div>
      )}

      {/* Image Section */}
      {post.image_type && (
        <div className="mt-1">
          {imageStatus === "done" && post.image_type !== "carousel" && imageUrl && (
            <img
              src={imageUrl}
              alt={IMAGE_TYPE_LABELS[post.image_type] || "Post visual"}
              className="w-full border-y border-border object-cover"
            />
          )}
          {imageStatus === "done" && post.image_type === "carousel" && carouselUrls.length > 0 && (
            <div className="relative">
              <img
                src={carouselUrls[carouselIdx]}
                alt={`Slide ${carouselIdx + 1} of ${carouselUrls.length}`}
                className="w-full border-y border-border object-cover"
              />
              {carouselUrls.length > 1 && (
                <>
                  <button
                    onClick={() => setCarouselIdx(i => Math.max(0, i - 1))}
                    disabled={carouselIdx === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 disabled:opacity-30 transition-opacity"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCarouselIdx(i => Math.min(carouselUrls.length - 1, i + 1))}
                    disabled={carouselIdx === carouselUrls.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 disabled:opacity-30 transition-opacity"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className="flex justify-center gap-1.5 mt-2 pb-2">
                    {carouselUrls.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCarouselIdx(i)}
                        className={`w-2 h-2 rounded-full transition-colors ${i === carouselIdx ? "bg-primary" : "bg-muted-foreground/30"}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {imageStatus !== "done" && (
            <div className="px-4 pb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateImage}
                disabled={isGenerating || imageStatus === "generating"}
                className="w-full border-dashed"
              >
                {isGenerating || imageStatus === "generating" ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating {IMAGE_TYPE_LABELS[post.image_type]}…
                  </>
                ) : (
                  <>
                    <Image className="w-4 h-4 mr-2" />
                    Generate {IMAGE_TYPE_LABELS[post.image_type]}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* LinkedIn-style action bar */}
      <div className="border-t border-border mx-4" />
      <div className="flex items-center px-2 py-1">
        <button
          onClick={() => onCopy(post)}
          data-testid={`copy-btn-${post.id}`}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-colors ${
            copiedId === post.id
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          {copiedId === post.id ? (
            <Check className="w-5 h-5" />
          ) : (
            <Copy className="w-5 h-5" />
          )}
          <span className="hidden sm:inline">{copiedId === post.id ? "Copied!" : "Copy"}</span>
        </button>

        <button
          onClick={() => onMarkUsed(post)}
          data-testid={`mark-used-btn-${post.id}`}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Mark as used"
        >
          <BookOpen className="w-5 h-5" />
          <span className="hidden sm:inline">Used</span>
        </button>

        <button
          onClick={() => onSkip(post)}
          data-testid={`skip-btn-${post.id}`}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Skip"
        >
          <SkipForward className="w-5 h-5" />
          <span className="hidden sm:inline">Skip</span>
        </button>
      </div>
    </div>
  );
};

const PostSkeleton = () => (
  <div className="bg-card rounded-lg border border-border overflow-hidden">
    <div className="flex items-start gap-3 px-4 pt-3 pb-2">
      <Skeleton className="w-12 h-12 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
    <div className="px-4 py-2 space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
    <div className="border-t border-border mx-4" />
    <div className="flex gap-2 px-4 py-3">
      <Skeleton className="h-8 flex-1 rounded-lg" />
      <Skeleton className="h-8 flex-1 rounded-lg" />
      <Skeleton className="h-8 flex-1 rounded-lg" />
    </div>
  </div>
);

const FeedPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [posts, setPosts] = useState([]);
  const [userTopics, setUserTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState({ posts_used: 0, posts_available: 0 });
  const [isGenerating, setIsGenerating] = useState(false);

  const fetchPosts = useCallback(async (pageNum = 1, topic = null, append = false, section = null) => {
    try {
      if (pageNum === 1) setIsLoading(true);
      else setIsLoadingMore(true);

      const params = new URLSearchParams({ page: pageNum, limit: 10 });
      if (section) params.append("section", section);
      else if (topic) params.append("topic", topic);

      const response = await api.get(`/posts?${params}`);
      const newPosts = response.data.posts;

      if (append) {
        setPosts((prev) => [...prev, ...newPosts]);
      } else {
        setPosts(newPosts);
      }

      setHasMore(response.data.has_more);
      setPage(pageNum);
      setIsGenerating(response.data.generating || false);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
      toast.error("Failed to load posts");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);


  const fetchUserTopics = useCallback(async () => {
    try {
      const response = await api.get("/user/topics");
      setUserTopics(response.data.topics);
    } catch (error) {
      console.error("Failed to fetch user topics:", error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get("/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchUserTopics();
    fetchStats();
  }, [fetchPosts, fetchUserTopics, fetchStats]);

  // Poll every 10 seconds while posts are being generated
  useEffect(() => {
    if (!isGenerating || posts.length > 0) return;
    const interval = setInterval(() => {
      fetchPosts();
    }, 10000);
    return () => clearInterval(interval);
  }, [isGenerating, posts.length, fetchPosts]);

  const handleCopy = async (post) => {
    try {
      await navigator.clipboard.writeText(cleanPostContent(post.variant_content));
      setCopiedId(post.id);

      // Mark as used
      await api.post(`/posts/${post.id}/use`);

      // Remove from feed
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success("Post copied to clipboard!");
      fetchStats();

      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy post");
    }
  };

  const handleMarkUsed = async (post) => {
    try {
      await api.post(`/posts/${post.id}/use`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success("Post marked as used");
      fetchStats();
    } catch (error) {
      console.error("Failed to mark as used:", error);
      toast.error("Failed to mark post as used");
    }
  };

  const handleSkip = async (post) => {
    try {
      await api.post(`/posts/${post.id}/skip`);
      // Move post to end of list
      setPosts((prev) => {
        const filtered = prev.filter((p) => p.id !== post.id);
        return [...filtered, post];
      });
      toast.info("Post skipped");
    } catch (error) {
      console.error("Failed to skip:", error);
    }
  };

  const handleTopicFilter = (topic) => {
    setSelectedTopic(topic);
    if (topic === "__others") {
      fetchPosts(1, null, false, "others");
    } else {
      fetchPosts(1, topic);
    }
  };

  const handleLoadMore = () => {
    if (selectedTopic === "__others") {
      fetchPosts(page + 1, null, true, "others");
    } else {
      fetchPosts(page + 1, selectedTopic, true);
    }
  };

  const handleRefresh = () => {
    if (selectedTopic === "__others") {
      fetchPosts(1, null, false, "others");
    } else {
      fetchPosts(1, selectedTopic);
    }
    fetchStats();
    toast.success("Feed refreshed");
  };

  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="PostWriter" className="w-8 h-8" />
              <span className="font-semibold text-lg tracking-tight">PostWriter</span>
            </div>

            <div className="flex items-center gap-2">
              {/* Stats */}
              <div className="hidden sm:flex items-center gap-4 mr-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  <span>{stats.posts_used} used</span>
                </div>
                <div className="flex items-center gap-1">
                  <Sparkles className="w-4 h-4" />
                  <span>{stats.posts_available} available</span>
                </div>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                data-testid="refresh-btn"
                title="Refresh feed"
              >
                <RefreshCw className="w-5 h-5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-medium">
                        {user?.name?.charAt(0) || "U"}
                      </div>
                    )}
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="font-medium text-sm">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/shortlist")} data-testid="shortlist-link" className="font-medium text-primary">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Repurposing Vault
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/history")} data-testid="history-link">
                    <History className="w-4 h-4 mr-2" />
                    Post History
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-link">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Topic Filter */}
        {userTopics.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <Button
              variant={selectedTopic === null ? "default" : "outline"}
              size="sm"
              onClick={() => handleTopicFilter(null)}
              data-testid="filter-all"
              className={selectedTopic === null ? "bg-primary hover:bg-primary/90" : ""}
            >
              All Topics
            </Button>
            {userTopics.map((topic) => (
              <Button
                key={topic}
                variant={selectedTopic === topic ? "default" : "outline"}
                size="sm"
                onClick={() => handleTopicFilter(topic)}
                data-testid={`filter-${topic}`}
                className={selectedTopic === topic ? "bg-primary hover:bg-primary/90" : ""}
              >
                {TOPIC_LABELS[topic] || topic}
              </Button>
            ))}
            <Button
              variant={selectedTopic === "__others" ? "default" : "outline"}
              size="sm"
              onClick={() => handleTopicFilter("__others")}
              data-testid="filter-others"
              className={selectedTopic === "__others" ? "bg-primary hover:bg-primary/90" : ""}
            >
              Others
            </Button>
          </div>
        )}

        {/* Posts Grid */}
        {isLoading ? (
          <div className="max-w-[555px] mx-auto space-y-4">
            {[...Array(3)].map((_, i) => (
              <PostSkeleton key={i} />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            {isGenerating ? (
              <>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Generating your posts</h3>
                <p className="text-muted-foreground mb-2">
                  We're crafting personalized LinkedIn posts based on your topics and preferences.
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  This usually takes a few minutes. Refresh the page to check for new posts.
                </p>
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No posts available</h3>
                <p className="text-muted-foreground mb-4">
                  You've gone through all the posts for your selected topics!
                </p>
                <Button
                  onClick={() => navigate("/settings")}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Topics
                </Button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="max-w-[555px] mx-auto space-y-4">
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  user={user}
                  onCopy={handleCopy}
                  onMarkUsed={handleMarkUsed}
                  onSkip={handleSkip}
                  copiedId={copiedId}
                />
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  data-testid="load-more-btn"
                  className="px-8"
                >
                  {isLoadingMore ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load More Posts"
                  )}
                </Button>
              </div>
            )}

          </>
        )}
      </main>
    </div>
  );
};

export default FeedPage;
